import { useNavigate } from 'react-router-dom';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Header from '@cloudscape-design/components/header';
import Cards from '@cloudscape-design/components/cards';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Link from '@cloudscape-design/components/link';
import Badge from '@cloudscape-design/components/badge';
import Tabs from '@cloudscape-design/components/tabs';
import ProgressBar from '@cloudscape-design/components/progress-bar';

// API and hooks
import { usersApi, DashboardProgram, DashboardCohortDetail } from '../api';
import { useAuthStore } from '../stores/authStore';
import { canCreateProgram } from '../utils/permissions';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorAlert } from '../components/ErrorAlert';
import { UserLink } from '../components/UserLink';
import { phoneToolUrl } from '../lib/externalUrls';
import { useState, useEffect, useRef } from 'react';

// Type definitions (using API types directly)
// DashboardProgram and DashboardCohortDetail are imported from API

// Format date for badges (e.g., "3/15" or "12/31")
const formatBadgeDate = (dateStr: string): string => {
  const [, month, day] = dateStr.split('-').map(Number);
  return `${month}/${day}`;
};

// Format ended date with 2-digit year (e.g., "3/15/24")
const formatEndedDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return `${month}/${day}/${String(year).slice(2)}`;
};

// Parse date string as local date for consistent sorting
const parseLocalDate = (dateStr: string): number => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).getTime();
};

// Empty State Component
interface EmptyStateProps {
  canCreateProgram: boolean;
}

function EmptyState({ canCreateProgram }: EmptyStateProps) {
  const navigate = useNavigate();

  return (
    <Box textAlign="center" padding="xxl">
      <SpaceBetween size="m">
        <Box variant="h2">Welcome to Cohort</Box>
        <Box color="text-body-secondary">
          Browse available training programs to find learning and mentoring opportunities.
        </Box>
        {canCreateProgram &&
          <Box color="text-body-secondary">
            As a manager, you can also create a new training program.
          </Box>
        }
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
          <Button variant="primary" onClick={() => navigate('/programs')}>
            Browse Programs
          </Button>
          {canCreateProgram && (
            <Button variant="normal" onClick={() => navigate('/programs/create')}>
              Create Program
            </Button>
          )}
        </div>
      </SpaceBetween>
    </Box>
  );
}

// Main Dashboard Component
function Dashboard() {
  const currentUser = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<DashboardProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // State for cohort tab - will be set after data loads
  const [cohortTabId, setCohortTabId] = useState<string>('');
  const defaultTabSetRef = useRef(false);

  // Fetch dashboard data (single API call)
  useEffect(() => {
    if (!currentUser?.userId) return;
    async function fetchDashboardData() {
      try {
        setLoading(true);
        setError(null);
        
        const dashboardData = await usersApi.getDashboardData(currentUser?.userId ?? '');
        
        // Sort programs by role priority (admin > mentor > participant)
        const sortedPrograms = dashboardData.programs.sort((a, b) => {
          const roleWeight = (roles: string[]) => {
            if (roles.includes('owner') || roles.includes('admin')) return 3;
            if (roles.includes('mentor')) return 2;
            if (roles.includes('participant')) return 1;
            return 0;
          };
          const weightDiff = roleWeight(b.roles) - roleWeight(a.roles);
          if (weightDiff !== 0) return weightDiff;
          
          // Secondary sort: alphabetical by program name
          return a.programName.localeCompare(b.programName);
        });

        setPrograms(sortedPrograms);
        
        // Default to the first visible tab in priority order: enrolled → mentoring → managing → past
        if (!defaultTabSetRef.current) {
          defaultTabSetRef.current = true;
          const allCohorts = sortedPrograms.flatMap(p => p.cohorts);
          const activeCohorts = allCohorts.filter(c => c.status === 'active' || c.status === 'upcoming' || c.status === 'registration-open');

          const hasEnrolled = activeCohorts.some(c => c.userRoles.includes('participant') && !c.userRoles.includes('owner') && !c.userRoles.includes('admin') && !c.userRoles.includes('mentor'));
          const hasMentoring = activeCohorts.some(c => c.userRoles.includes('mentor') && !c.userRoles.includes('owner') && !c.userRoles.includes('admin'));
          const hasManaging = activeCohorts.some(c => c.userRoles.includes('owner') || c.userRoles.includes('admin'));
          const hasPast = allCohorts.some(c => c.status === 'completed' || c.status === 'archived');

          if (hasEnrolled) {
            setCohortTabId('enrolled-cohorts');
          } else if (hasMentoring) {
            setCohortTabId('mentoring-cohorts');
          } else if (hasManaging) {
            setCohortTabId('managing-cohorts');
          } else if (hasPast) {
            setCohortTabId('past-cohorts');
          }
        }

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardData();
  }, [currentUser?.userId]); // Only re-fetch when userId changes

  if (!currentUser) return null;

  // Show loading state
  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  // Show error state
  if (error) {
    return (
      <ErrorAlert 
        error={error} 
        onRetry={() => window.location.reload()}
      />
    );
  }

  // Show empty state if user has no programs
  if (programs.length === 0) {
    return (
      <SpaceBetween size="l">
        <Header variant="h1">Dashboard</Header>
        <EmptyState canCreateProgram={canCreateProgram(currentUser)} />
      </SpaceBetween>
    );
  }

  // Separate programs by admin/owner role
  const adminPrograms = programs.filter(p => p.roles.includes('admin') || p.roles.includes('owner'));
  
  // Flatten all cohorts from all programs
  type CohortWithProgram = DashboardCohortDetail & {
    programId: string;
    programDescription: string;
  };
  
  const allCohorts: CohortWithProgram[] = programs.flatMap(program => 
    program.cohorts.map(cohort => ({
      ...cohort,
      programId: program.programId,
      programDescription: program.description,
    }))
  );

  // Split cohorts into active and past
  const activeCohorts = allCohorts.filter(c => c.status === 'active' || c.status === 'upcoming' || c.status === 'registration-open');
  const pastCohorts = allCohorts.filter(c => c.status === 'completed' || c.status === 'archived');

  // Further split active cohorts by role
  const managingCohorts = activeCohorts.filter(c => c.userRoles.includes('owner') || c.userRoles.includes('admin'));
  const mentoringCohorts = activeCohorts.filter(c => c.userRoles.includes('mentor') && !c.userRoles.includes('owner') && !c.userRoles.includes('admin'));
  const enrolledCohorts = activeCohorts.filter(c => c.userRoles.includes('participant') && !c.userRoles.includes('owner') && !c.userRoles.includes('admin') && !c.userRoles.includes('mentor'));
  // Sort cohorts by role priority, then by date
  const sortCohortsByRole = (cohorts: CohortWithProgram[]) => {
    return [...cohorts].sort((a, b) => {
      // Role priority: admin > mentor > participant
      const getRolePriority = (roles: string[]) => {
        if (roles.includes('owner') || roles.includes('admin')) return 3;
        if (roles.includes('mentor')) return 2;
        if (roles.includes('participant')) return 1;
        return 0;
      };
      
      const aPriority = getRolePriority(a.userRoles);
      const bPriority = getRolePriority(b.userRoles);
      
      if (aPriority !== bPriority) {
        return bPriority - aPriority; // Higher priority first
      }
      
      // Within same role, sort by date
      // Active: sort by end date (ending soonest first)
      // Upcoming: sort by start date (starting soonest first)
      if (a.status === 'active' && b.status === 'active') {
        return parseLocalDate(a.endDate) - parseLocalDate(b.endDate);
      }
      if (a.status === 'upcoming' && b.status === 'upcoming') {
        return parseLocalDate(a.startDate) - parseLocalDate(b.startDate);
      }
      // Active before upcoming
      if (a.status === 'active') return -1;
      if (b.status === 'active') return 1;
      
      return 0;
    });
  };

  const sortedManagingCohorts = sortCohortsByRole(managingCohorts);
  const sortedMentoringCohorts = sortCohortsByRole(mentoringCohorts);
  const sortedEnrolledCohorts = sortCohortsByRole(enrolledCohorts);
  const sortedPastCohorts = [...pastCohorts].sort((a, b) => parseLocalDate(b.endDate) - parseLocalDate(a.endDate));

  // Render program cards for "Programs You Manage" section
  const renderAdminProgramCards = (programList: DashboardProgram[]) => {
    return (
      <Cards
        cardDefinition={{
          header: (item) => (
            <Link 
              fontSize="heading-m" 
              href="#"
              onFollow={(e) => {
                e.preventDefault();
                navigate(`/programs/${item.programId}`);
              }}
            >
              {item.programName}
            </Link>
          ),
          sections: [
            {
              id: 'content',
              content: (item) => (
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: '80px', paddingTop: '8px' }}>
                  <div style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: 'var(--color-text-body-secondary)',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    marginBottom: '6px',
                  }}>
                    {item.description}
                  </div>
                  <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <Button
                        variant="normal"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/programs/${item.programId}`);
                        }}
                      >
                        View Program
                      </Button>
                      {/* <Button
                        iconName="add-plus"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/programs/${item.programId}/cohorts/create`);
                        }}
                      >
                        Create Cohort
                      </Button> */}
                    </div>
                  </div>
                </div>
              ),
            },
          ],
        }}
        cardsPerRow={[
          { cards: 1 },
          { minWidth: 500, cards: 2 },
          { minWidth: 1000, cards: 3 }
        ]}
        items={programList}
        trackBy="programId"
        empty={
          <Box textAlign="center" color="inherit">
            <Box variant="p" color="inherit">No programs</Box>
          </Box>
        }
      />
    );
  };

  // Render cohort cards with role-specific information
  const renderCohortCards = (cohortList: CohortWithProgram[], isActive: boolean) => {
    return (
      <Cards
        cardDefinition={{
          header: (item) => (
            <SpaceBetween size="xxs">
              <Box fontSize="heading-m" color="text-body-secondary" fontWeight="bold">{item.programName}</Box>
              <Link 
                fontSize="heading-m" 
                href="#"
                onFollow={(e) => {
                  e.preventDefault();
                  navigate(`/cohorts/${item.cohortId}`);
                }}
              >
                {item.cohortName}
              </Link>
            </SpaceBetween>
          ),
          sections: [
            {
              id: 'content',
              content: (item) => {
                const itemIsActive = isActive;
                return (
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: '140px', paddingTop: '8px' }}>
                  {/* Cohort Description - Truncated to 3 lines */}
                  <div style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: 'var(--color-text-body-secondary)',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    marginBottom: '6px',
                  }}>
                    {item.description}
                  </div>
                  
                  <div style={{ marginTop: 'auto', paddingTop: '12px' }}>
                    <SpaceBetween size="s">
                      {/* Date and Status Badges - Direct children for proper spacing */}
                      <div style={{ marginBottom: '8px' }}>
                        <SpaceBetween direction="horizontal" size="xs">
                          {/* {isActive && (
                            <Badge color="green">{item.status === 'upcoming' ? 'Upcoming' : 'In Progress'}</Badge>
                          )} */}
                          {itemIsActive && (
                            <Badge color="green">{item.status === 'upcoming' ? `Starts ${formatBadgeDate(item.startDate)}` : `Ends ${formatBadgeDate(item.endDate)}`}</Badge>
                          )}
                          {!itemIsActive && (
                            <Badge>{item.status === 'completed' ? 'Completed' : 'Inactive'}</Badge>
                          )}
                          {!itemIsActive && (
                            <Badge>Ended {formatEndedDate(item.endDate)}</Badge>
                          )}
                        </SpaceBetween>
                      </div>
                      
                      {/* Admin Pending Actions */}
                      {item.roleData?.adminData && (
                        <Box>
                          <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Pending Actions</Box>
                          {(item.roleData.adminData.pendingMentorRequests > 0 || 
                            item.roleData.adminData.pendingGraduations > 0 || 
                            item.roleData.adminData.unassignedParticipants > 0) ? (
                            <SpaceBetween size="xs">
                              {item.roleData.adminData.pendingMentorRequests > 0 && (
                                <Link
                                  fontSize="body-s"
                                  href="#"
                                  onFollow={(e) => {
                                    e.preventDefault();
                                    navigate(`/cohorts/${item.cohortId}`);
                                  }}
                                >
                                  {item.roleData.adminData.pendingMentorRequests} mentor {item.roleData.adminData.pendingMentorRequests === 1 ? 'request' : 'requests'}
                                </Link>
                              )}
                              {item.roleData.adminData.unassignedParticipants > 0 && (
                                <Link
                                  fontSize="body-s"
                                  href="#"
                                  onFollow={(e) => {
                                    e.preventDefault();
                                    navigate(`/cohorts/${item.cohortId}`);
                                  }}
                                >
                                  {item.roleData.adminData.unassignedParticipants} mentor {item.roleData.adminData.unassignedParticipants === 1 ? 'assignment' : 'assignments'} needed
                                </Link>
                              )}
                              {item.roleData.adminData.pendingGraduations > 0 && (
                                <Link
                                  fontSize="body-s"
                                  href="#"
                                  onFollow={(e) => {
                                    e.preventDefault();
                                    navigate(`/cohorts/${item.cohortId}`);
                                  }}
                                >
                                  {item.roleData.adminData.pendingGraduations} graduation {item.roleData.adminData.pendingGraduations === 1 ? 'approval' : 'approvals'}
                                </Link>
                              )}
                            </SpaceBetween>
                          ) : (
                            <Box fontSize="body-s" color="text-body-secondary">No pending actions</Box>
                          )}
                        </Box>
                      )}
                      
                      {/* Mentor Pending Activity Reviews */}
                      {item.roleData?.mentorData && (
                        <Box>
                          <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Pending Actions</Box>
                          {item.roleData.mentorData.pendingActivityReviews > 0 ? (
                            <Link
                              fontSize="body-s"
                              href="#"
                              onFollow={(e) => {
                                e.preventDefault();
                                navigate(`/cohorts/${item.cohortId}`);
                              }}
                            >
                              {item.roleData.mentorData.pendingActivityReviews} {item.roleData.mentorData.pendingActivityReviews === 1 ? 'activity' : 'activities'} awaiting your review
                            </Link>
                          ) : (
                            <Box fontSize="body-s" color="text-body-secondary">No pending actions</Box>
                          )}
                        </Box>
                      )}
                      
                      {/* Participant Progress */}
                      {item.roleData?.participantProgress && (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          padding: '0px',
                          overflow: 'hidden'
                        }}>
                          <div style={{ width: '120px', flexShrink: 0, fontWeight: 'bold', fontSize: '14px' }}>Your Progress</div>
                          <div style={{ width: '40px', fontSize: '12px', fontFamily: '"Open Sans", "Helvetica Neue", Roboto, Arial, sans-serif', textAlign: 'center', flexShrink: 0, color: '#5f6b7a' }}>
                            {item.roleData.participantProgress.completedActivities}/{item.roleData.participantProgress.totalActivities}
                          </div>
                          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <ProgressBar 
                              value={item.roleData.participantProgress.percentage} 
                              status={item.roleData.participantProgress.percentage === 100 ? "success" : "in-progress"}
                              resultText={item.roleData.participantProgress.percentage === 100 ? "Complete" : undefined}
                              variant="standalone" 
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Participant's Mentor Info - Simplified */}
                      {item.userRoles.includes('participant') && !item.userRoles.includes('owner') && !item.userRoles.includes('admin') && !item.userRoles.includes('mentor') && (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          fontSize: '14px'
                        }}>
                          <span style={{ width: '120px', flexShrink: 0, fontWeight: 'bold' }}>Your Mentor</span>
                          <span>
                            {item.roleData?.mentorInfo?.mentorName && item.roleData?.mentorInfo?.mentorId
                              ? <UserLink 
                                  userName={item.roleData.mentorInfo.mentorName} 
                                  userId={item.roleData.mentorInfo.mentorId} 
                                />
                              : 'Not yet assigned'}
                          </span>
                        </div>
                      )}
                      
                      {/* Mentor's Mentees Progress */}
                      {item.roleData?.mentorData && item.roleData.mentorData.mentees.length > 0 && (
                        <Box>
                          <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Your Mentees ({item.roleData.mentorData.menteeCount})</Box>
                          <SpaceBetween size="xs">
                            {item.roleData.mentorData.mentees.map((mentee) => (
                              <div 
                                key={mentee.userId}
                                style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  padding: '0px',
                                  backgroundColor: '#ffffff',
                                  overflow: 'hidden'
                                }}
                              >
                                <div style={{ width: '120px', flexShrink: 0 }}>
                                  <Link 
                                    href={phoneToolUrl(mentee.userId)}
                                    target="_blank"
                                    fontSize="body-s"
                                  >
                                    {mentee.userName}
                                  </Link>
                                </div>
                                <div style={{ width: '40px', fontSize: '12px', fontFamily: '"Open Sans", "Helvetica Neue", Roboto, Arial, sans-serif', textAlign: 'center', flexShrink: 0, color: '#5f6b7a' }}>
                                  {mentee.completedActivities}/{mentee.totalActivities}
                                </div>
                                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'left', gap: '0px', overflow: 'hidden' }}>
                                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                    <ProgressBar 
                                      value={mentee.percentage} 
                                      status={mentee.percentage === 100 ? "success" : "in-progress"}
                                      resultText={mentee.percentage === 100 ? "Complete" : undefined}
                                      variant="standalone" 
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </SpaceBetween>
                        </Box>
                      )}
                      
                      {/* Admin Actions - Commented out for now
                      {(item.userRoles.includes('owner') || item.userRoles.includes('admin')) && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '4px' }}>
                          <Button
                            variant="normal"
                            iconName="edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/cohorts/${item.cohortId}`);
                            }}
                          >
                            Manage Cohort
                          </Button>
                        </div>
                      )}
                      */}
                    </SpaceBetween>
                  </div>
                </div>
                );
              },
            },
          ],
        }}
        cardsPerRow={[
          { cards: 1 },
          { minWidth: 500, cards: 2 },
          { minWidth: 1000, cards: 3 }
        ]}
        items={cohortList}
        trackBy="cohortId"
        empty={
          <Box textAlign="center" color="inherit">
            <Box variant="p" color="inherit">
              {isActive === false ? 'No past cohorts' : 'No cohorts'}
            </Box>
          </Box>
        }
      />
    );
  };

  return (
    <SpaceBetween size="l">
      <Header 
        variant="h1"
        actions={
          currentUser?.isManager ? (
            <Button
              variant="normal"
              iconName="add-plus"
              onClick={() => navigate('/programs/create')}
            >
              Create Program
            </Button>
          ) : undefined
        }
      >
        Dashboard
      </Header>
      
      {/* Programs You Manage Section (only if user is admin of any programs) */}
      {adminPrograms.length > 0 && (
        <SpaceBetween size="l">
          <Header variant="h2">Programs You Manage</Header>
          {renderAdminProgramCards(adminPrograms)}
        </SpaceBetween>
      )}

      {/* Your Cohorts Section */}
      <SpaceBetween size="xs">
        <Header variant="h2">Your Cohorts</Header>
        <Tabs
          activeTabId={cohortTabId}
          onChange={({ detail }) => setCohortTabId(detail.activeTabId)}
          tabs={[
            ...(sortedEnrolledCohorts.length > 0 ? [{
              id: 'enrolled-cohorts',
              label: `Enrolled (${sortedEnrolledCohorts.length})`,
              content: renderCohortCards(sortedEnrolledCohorts, true),
            }] : []),
            ...(sortedMentoringCohorts.length > 0 ? [{
              id: 'mentoring-cohorts',
              label: `Mentoring (${sortedMentoringCohorts.length})`,
              content: renderCohortCards(sortedMentoringCohorts, true),
            }] : []),
            ...(sortedManagingCohorts.length > 0 ? [{
              id: 'managing-cohorts',
              label: `Managing (${sortedManagingCohorts.length})`,
              content: renderCohortCards(sortedManagingCohorts, true),
            }] : []),
            ...(sortedPastCohorts.length > 0 ? [{
              id: 'past-cohorts',
              label: `Past (${sortedPastCohorts.length})`,
              content: renderCohortCards(sortedPastCohorts, false),
            }] : []),
          ]}
        />
      </SpaceBetween>
    </SpaceBetween>
  );
}

export default Dashboard;
